import { useEffect, useMemo } from 'react';
import { singletonHook } from 'react-singleton-hook';
import useUserDetails from '../user/hooks/useUserDetails';
import useUserDetailsStore from '../../../stores/ResidentDetails/residentDetails';
import {
  useLandaApiMutation,
  useLandaAuthedApiLazyQuery,
} from '../../api/graphql/landa_api';
import { RESIDENT_DETAILS_QUERY } from '../../../graphql/resident/residentDetailsQuery';
import { OptionsType } from '../../api/graphql/functions/useLandaLazyQuery';
import {
  ResidentDetailsTypes,
  UnpaidRentPayment,
} from '../../../types/resident-details-types';
import { QueryResult } from '@apollo/client';
import { OperationVariables } from '@apollo/client/core';
import { AUTOPAY_MUTATION } from '../../../graphql/resident/autopayMutation';
import moment from 'moment';

const init: {
  residentDetails: ResidentDetailsTypes;
  request: (() => Promise<QueryResult<any, OperationVariables>>) | undefined;
  isActiveLeaseEviction: boolean;
  loading: boolean;
  setAutopay?: (newValue: boolean) => Promise<boolean | undefined>;
  failedPayments: UnpaidRentPayment[];
} = {
  residentDetails: {
    isAutoPayEnabled: false,
    renewalUnitsTicker: [],
    activeLease: null,
  },
  isActiveLeaseEviction: false,
  request: undefined,
  loading: false,
  setAutopay: undefined,
  failedPayments: [],
};

const useResidentDetails = () => {
  const { isTenant } = useUserDetails();
  const { residentDetails, setResidentDetails, setAutopay } =
    useUserDetailsStore();

  const [innerRequest, { loading }] = useLandaAuthedApiLazyQuery(
    RESIDENT_DETAILS_QUERY
  );

  const [autoPay] = useLandaApiMutation(AUTOPAY_MUTATION);

  const request = async (innerConfig: OptionsType = {}) => {
    const response = await innerRequest(innerConfig);
    setResidentDetails({
      ...response?.data?.resident,
    });
    return response;
  };

  const inerSetAutopay = async (newValue: boolean) => {
    if (!isTenant) return;
    try {
      const response = await autoPay({
        variables: {
          isAutoPayEnabled: newValue,
        },
      });

      if (response?.data?.changeAutoPayEnabled?.ok) {
        setAutopay(newValue);
        return newValue;
      } else {
        return residentDetails?.isAutoPayEnabled;
      }
    } catch (error) {
      return residentDetails?.isAutoPayEnabled;
    }
  };

  const failedPayments = useMemo(() => {
    const unpaidRentPayments =
      residentDetails?.activeLease?.unpaidRentPayment || [];

    return unpaidRentPayments.filter((payment) => {
      const isPastDue = moment(payment.dateDue).isBefore(moment());
      const isUnpaid = payment.status === 'failed';

      return isPastDue && isUnpaid;
    });
  }, [residentDetails]);

  const isActiveLeaseEviction = useMemo(() => {
    return residentDetails?.activeLease?.isInEviction || false;
  }, [residentDetails]);

  useEffect(() => {
    if (!isTenant) return;
    request();
  }, [isTenant]);

  return {
    request,
    residentDetails: residentDetails || init.residentDetails,
    isActiveLeaseEviction,
    loading: loading && !residentDetails,
    setAutopay: inerSetAutopay,
    failedPayments,
  };
};

export default singletonHook(init, useResidentDetails);
